import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { useGetFolderStatusAndItsTypologyAsync } from '@/features/easy/shared/apis';
import { CROSSING_DIRECTION_COUNTRY } from '@/features/shared/constants';
import { isBorderCrossingRoRo, OPEN_CUSTOMS_AND_CROSSING } from '@/features/shared/helpers/typologies';

import { REGISTER_NEW_FOLDER_FORM, RegisterNewFolderForm } from '../types';
import { useCreateFolderByTypology } from './useCreateFolderByTypology';

export const usePreFolderQualificationWithRORO = () => {
  const { folderId } = useParams();
  const { data: folderStatusAndItsTypologyData, isLoading: isFolderStatusAndItsTypologyDataLoading } =
    useGetFolderStatusAndItsTypologyAsync({
      folderId,
    });

  const { isCreateNewFolderInError, isAddNewFolderByTypologyPending, handleCreateNewFolder, handleCloseToaster } =
    useCreateFolderByTypology();

  const direction = folderStatusAndItsTypologyData?.direction;
  const schema = useFormSchema({ direction });
  const { control, formState, handleSubmit, register } = useForm<RegisterNewFolderForm>({
    mode: 'all',
    defaultValues: {
      folderTypologyId: null,
      vehicleNationality: null,
      containsEmptyPackages: null,
    },
    resolver: yupResolver(schema),
  });

  const handleSubmitNewFolder = handleSubmit(handleCreateNewFolder(direction));

  return {
    isFolderStatusAndItsTypologyDataLoading,
    isAddNewFolderByTypologyPending,
    isCreateNewFolderInError,
    direction,
    formState,
    control,
    register,
    handleCloseToaster,
    handleSubmitNewFolder,
  };
};

function useFormSchema({ direction }: { direction: string }) {
  const { t } = useTranslation('borderPassEasyWithRORO_prefolderQualification');

  return Yup.object().shape({
    internalClientReference: Yup.string()
      .trim()
      .matches(
        /^[a-zA-Z0-9 ]*$/,
        t('borderPassEasyWithRORO.prefolderQualification.form.alphanumeric.pattern.errorMessage'),
      )
      .max(60, t('borderPassEasyWithRORO.prefolderQualification.form.maxLength.error.message', { maxLength: 60 })),
    folderTypologyId: Yup.string()
      .nullable()
      .required(t('borderPassEasyWithRORO.prefolderQualification.form.fieldIsMandatory.errorMessage')),
    containsEmptyPackages: Yup.string()
      .nullable()
      .when(REGISTER_NEW_FOLDER_FORM.folderTypologyId, {
        is: (value: string) => value === OPEN_CUSTOMS_AND_CROSSING.BORDER_CROSSING_RORO_EMPTY_V2,
        then: Yup.string()
          .nullable()
          .required(t('borderPassEasyWithRORO.prefolderQualification.form.fieldIsMandatory.errorMessage')),
      }),
    haulierGbEoriNumber: Yup.string().when(REGISTER_NEW_FOLDER_FORM.folderTypologyId, {
      is: (folderTypologyId: string) =>
        isBorderCrossingRoRo({ folderTypologyId }) && direction === CROSSING_DIRECTION_COUNTRY.EUUK,
      then: (schema) =>
        schema
          .required(t('borderPassEasyWithRORO.prefolderQualification.form.fieldIsMandatory.errorMessage'))
          .matches(
            /^GB\d{12}$/,
            t('borderPassEasyWithRORO.prefolderQualification.form.haulierGbEoriNumber.regex.error.message', {
              prefix: 'GB',
              lengthOfDigit: 12,
            }),
          )
          .transform((value: string) => value?.replace(/\s+/g, '')),
      otherwise: (schema) => schema.notRequired(),
    }),
    haulierEuEoriNumber: Yup.string().when(REGISTER_NEW_FOLDER_FORM.folderTypologyId, {
      is: (folderTypologyId: string) =>
        isBorderCrossingRoRo({ folderTypologyId }) && direction === CROSSING_DIRECTION_COUNTRY.UKEU,
      then: (schema) =>
        schema
          .required(t('borderPassEasyWithRORO.prefolderQualification.form.fieldIsMandatory.errorMessage'))
          .matches(
            /^(?!GB)[A-Z]{2}[A-Z0-9]{6,18}$/,
            t('borderPassEasyWithRORO.prefolderQualification.form.haulierEuEoriNumber.regex.error.message', {
              minOfLength: 8,
              maxOfLength: 20,
            }),
          )
          .transform((value: string) => value?.replace(/\s+/g, '')),
      otherwise: (schema) => schema.notRequired(),
    }),
    vehicleNationality: Yup.string()
      .nullable()
      .when(REGISTER_NEW_FOLDER_FORM.folderTypologyId, {
        is: (folderTypologyId: string) => isBorderCrossingRoRo({ folderTypologyId }),
        then: (schema) =>
          schema.required(t('borderPassEasyWithRORO.prefolderQualification.form.fieldIsMandatory.errorMessage')),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
}
